import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";

import Login from "./views/Login";
import Landing from "./views/Landing";
import Register from "./views/Register";
import ForgotPassword from "./views/ForgotPassword";
import CreateCompany from "./views/CreateCompany";
import Dashboard from "./views/Dashboard";
import Employees from "./views/Employees";
import Timesheet from "./views/Timesheet";
import CalendarPage from "./views/CalendarPage";
import Payroll from "./views/Payroll";
import Settings from "./views/Settings";
import Profile from "./views/Profile";
import Directory from "./views/Directory";
import Chat from "./views/Chat";
import Invitations from "./views/Invitations";
import AdminFeedback from "./views/AdminFeedback";
import AdminDashboard from "./views/AdminDashboard";
import AdminAnalytics from "./views/AdminAnalytics";
import AdminCompanies from "./views/AdminCompanies";
import AdminUsers from "./views/AdminUsers";
import Tasks from "./views/Tasks";
import Documents from "./views/Documents";
import Bonuses from "./views/Bonuses";
import Withholdings from "./views/Withholdings";
import VacationSchedule from "./views/VacationSchedule";
import Terms from "./views/Terms";
import Privacy from "./views/Privacy";
import "./App.css";
import { captureUtmFromUrl, trackPageView } from "./utils/analytics";

const Wrap = ({ children }) => <Layout>{children}</Layout>;

function InvitationsRoute() {
  const { user } = useAuth();
  const isHrAdmin = user && (user.role === "director" || user.role === "hr");
  if (isHrAdmin && user.company_id) {
    return <Navigate to="/employees?tab=invitations" replace />;
  }
  return <Wrap><Invitations /></Wrap>;
}

function AppRouter() {
  const location = useLocation();

  // Track SPA navigation in Yandex.Metrika + first-party analytics
  useEffect(() => {
    captureUtmFromUrl();
    const path = location.pathname + location.search + location.hash;
    const publicPaths = ["/", "/login", "/register", "/forgot-password", "/terms", "/privacy"];
    if (publicPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      trackPageView(path);
    }
    if (typeof window !== "undefined" && typeof window.ym === "function") {
      window.ym(109690764, "hit", path, {
        referer: document.referrer,
      });
    }
  }, [location.pathname, location.search, location.hash]);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route path="/create-company" element={
        <ProtectedRoute requireCompany={false}><CreateCompany /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><Wrap><Dashboard /></Wrap></ProtectedRoute>
      } />
      <Route path="/timesheet" element={
        <ProtectedRoute><Wrap><Timesheet /></Wrap></ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute allow={["director","hr"]}><Wrap><CalendarPage /></Wrap></ProtectedRoute>
      } />
      <Route path="/employees" element={
        <ProtectedRoute allow={["director","hr"]}><Wrap><Employees /></Wrap></ProtectedRoute>
      } />
      <Route path="/payroll" element={
        <ProtectedRoute><Wrap><Payroll /></Wrap></ProtectedRoute>
      } />
      <Route path="/taxes" element={
        <ProtectedRoute allow={["director","hr"]}><Wrap><Settings /></Wrap></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allow={["director","hr"]}><Wrap><Settings /></Wrap></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><Wrap><Profile /></Wrap></ProtectedRoute>
      } />
      <Route path="/directory" element={
        <ProtectedRoute><Wrap><Directory /></Wrap></ProtectedRoute>
      } />
      <Route path="/chat/:uid" element={
        <ProtectedRoute><Wrap><Chat /></Wrap></ProtectedRoute>
      } />
      <Route path="/invitations" element={
        <ProtectedRoute requireCompany={false}><InvitationsRoute /></ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute allow={["director","hr"]}>
          <Navigate to="/employees?tab=history" replace />
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute><Wrap><Tasks /></Wrap></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><Wrap><Documents /></Wrap></ProtectedRoute>
      } />
      <Route path="/bonuses" element={
        <ProtectedRoute><Wrap><Bonuses /></Wrap></ProtectedRoute>
      } />
      <Route path="/withholdings" element={
        <ProtectedRoute><Wrap><Withholdings /></Wrap></ProtectedRoute>
      } />
      <Route path="/vacation-schedule" element={
        <ProtectedRoute><Wrap><VacationSchedule /></Wrap></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireCompany={false}><Wrap><AdminDashboard /></Wrap></ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requireCompany={false}><Wrap><AdminAnalytics /></Wrap></ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute requireCompany={false}><Wrap><AdminCompanies /></Wrap></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute requireCompany={false}><Wrap><AdminUsers /></Wrap></ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute requireCompany={false}><Wrap><AdminFeedback /></Wrap></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
